/**
 * Die Helligkeitsspanne — zwei Grenzen der Aufsicht, ein Wert des Benutzers dazwischen.
 *
 * Reine Rechnerei: kein Gerätezugriff, kein React, kein Katalog. Der Zugriff auf den
 * Bildschirm steht in `platform/helligkeit.ts`, die Grenzen kommen aus dem Katalog
 * (`min_brightness`, `max_brightness`).
 *
 * ⚠️ **Alles in Prozent.** `UIScreen.main.brightness` rechnet in 0…1; das Teilen macht das
 * Swift-Plugin an genau einer Stelle. Überall sonst steht eine ganze Zahl von 10 bis 100.
 */

export interface Spanne {
  min: number;
  max: number;
}

/**
 * Zwei Grenzen zu einer Spanne.
 *
 * ⚠️ **Verdrehte Grenzen heilen, statt zu werfen.** Setzt die Aufsicht min 80 und max 50, ist
 * das ein Tippfehler — und eine App, die deshalb anhält, ist im Prüfungssaal der teurere
 * Fehler. Die Obergrenze wird auf die Untergrenze gehoben; heraus kommt eine Punktspanne, und
 * die zeigt die Oberfläche als festen Wert statt als Regler. Der Fehler ist damit sichtbar.
 */
export function spanne(min: number, max: number): Spanne {
  return { min, max: Math.max(min, max) };
}

/** Die gerundete Mitte. Rückfall, wenn sich die Helligkeit des Geräts nicht lesen lässt. */
export function mitte(s: Spanne): number {
  return Math.round((s.min + s.max) / 2);
}

/**
 * Einen Wert auf die Spanne beschneiden.
 *
 * ⚠️ **Beschneiden, nicht verwerfen** (Entscheidung des Nutzers, 2026-09-17). Bei einer Spanne
 * gibt es keinen falschen Wert, nur einen ausserhalb des Zauns: 140 wird 100, 5 wird 10.
 *
 * ⚠️ **Keine Zahl wird zur Mitte**, nicht zu `min`. Ein gescheitertes `lies()` am Gerät ist
 * kein Grund, den Bildschirm auf die dunkelste erlaubte Stufe zu stellen.
 */
export function begrenze(wert: number, s: Spanne): number {
  if (!Number.isFinite(wert)) return mitte(s);
  return Math.min(s.max, Math.max(s.min, Math.round(wert)));
}
